/**
 * The note form, opened AFTER the screenshot is captured so the stored image
 * shows the screen, not this modal.
 *
 * Reviewer identity is the logged-in user — PropCloudAI runs on personal
 * logins, so no name field is needed (unlike the shared-demo-login reference
 * implementation this was ported from).
 */
import React from 'react';
import { Loader2, X } from 'lucide-react';
import {
  REVIEW_CATEGORIES,
  REVIEW_SEVERITIES,
  type ReviewCategory,
  type ReviewSeverity,
} from '../../types/screenReview.types';

const CATEGORY_LABELS: Record<ReviewCategory, string> = {
  bug: 'Bug — something is broken',
  change: 'Change — do this differently',
  idea: 'Idea — new suggestion',
  question: 'Question — needs discussion',
};

interface Props {
  screenLabel: string;
  reviewerName: string;
  saving: boolean;
  error: string | null;
  onSave: (input: {
    body: string;
    category: ReviewCategory;
    severity: ReviewSeverity;
  }) => void;
  onCancel: () => void;
}

export const NoteComposer: React.FC<Props> = ({
  screenLabel,
  reviewerName,
  saving,
  error,
  onSave,
  onCancel,
}) => {
  const [body, setBody] = React.useState('');
  const [category, setCategory] = React.useState<ReviewCategory>('bug');
  const [severity, setSeverity] = React.useState<ReviewSeverity>('medium');
  const [localError, setLocalError] = React.useState<string | null>(null);

  const submit = (): void => {
    const text = body.trim();
    if (!text) {
      setLocalError('Write what should change — the drawing alone loses its meaning later.');
      return;
    }
    setLocalError(null);
    onSave({ body: text, category, severity });
  };

  return (
    <div
      data-review-overlay=""
      className="fixed inset-0 z-[10310] flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">Review note</h2>
            <p className="text-xs text-neutral-500">
              {screenLabel} · by {reviewerName}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            aria-label="Cancel note"
            className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label htmlFor="rev-body" className="mb-1 block text-sm font-medium text-neutral-700">
              Note
            </label>
            <textarea
              id="rev-body"
              rows={4}
              value={body}
              disabled={saving}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What's wrong here, or what should change — and why?"
              className="input-field"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rev-cat" className="mb-1 block text-sm font-medium text-neutral-700">
                Type
              </label>
              <select
                id="rev-cat"
                value={category}
                disabled={saving}
                onChange={(e) => setCategory(e.target.value as ReviewCategory)}
                className="input-field"
              >
                {REVIEW_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="rev-sev" className="mb-1 block text-sm font-medium text-neutral-700">
                Priority
              </label>
              <select
                id="rev-sev"
                value={severity}
                disabled={saving}
                onChange={(e) => setSeverity(e.target.value as ReviewSeverity)}
                className="input-field"
              >
                {REVIEW_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s[0].toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(localError || error) && (
            <p className="text-sm text-rose-700">{localError ?? error}</p>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900"
            >
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={saving} className="primary-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save note'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteComposer;
